/* ==========================================================================
   Custom cart drawer behaviour — ZwarteRoes
   Works WITH the native Luxx cart engine (theme.js: theme.CartDrawer /
   CartForm). That engine already handles: add-to-cart, quantity changes,
   removal, rebuilding the line items + subtotal, the header dot, opening the
   drawer on `ajaxProduct:added`, and firing `cart:updated`.

   This file only adds the bits the engine doesn't know about:
     1. The drawer header count badge ([data-cart-count]).
     2. The "+ TOEVOEGEN" upsell buttons.
     3. Re-rendering the upsell list when the cart changes (Section Rendering
        API), so the source collection + already-in-cart filtering stay fresh.
   ========================================================================== */
(function () {
  'use strict';

  var UPSELL_SECTION = 'cart-drawer-upsell';
  var SELECTORS = {
    countBadge: '[data-cart-count]',
    upsellContainer: '[data-cart-upsell-container]',
    upsellAdd: '.cart-upsell__add',
    upsellItem: '.cart-upsell__item',
    upsellOptionSelect: '.cart-upsell__option-select',
    upsellSheet: '[data-upsell-sheet]',
    sheetAdd: '.cart-upsell-sheet__add',
    quickAdd: '.js-quick-add',
    quickAddCard: '[data-variant-card]',
    removeLink: '.cart-item__delete, .cart__remove a',
    qtyAdjust: '.js-qty__adjust',
    qtyInput: '.js-qty__num'
  };

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }
  function priceHtml(v) {
    if (!v || !v.price) { return ''; }
    return v.compare
      ? '<span class="cart-upsell__price-sale">' + v.price + '</span> <s class="cart-upsell__price-compare">' + v.compare + '</s>'
      : v.price;
  }

  /* ---- Helpers ---------------------------------------------------------- */
  function updateCount(count) {
    document.querySelectorAll(SELECTORS.countBadge).forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-visible', count > 0);
    });
  }

  function fetchCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCount(cart.item_count);
        return cart;
      })
      .catch(function () { /* non-fatal */ });
  }

  // Re-render the upsell list with the current cart via the Section Rendering API.
  function refreshUpsell() {
    closeUpsellSheet();
    var container = document.querySelector(SELECTORS.upsellContainer);
    if (!container) { return Promise.resolve(); }

    return fetch('/?section_id=' + UPSELL_SECTION, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(SELECTORS.upsellContainer) || doc.querySelector('#cart-upsell-container');
        container.innerHTML = fresh ? fresh.innerHTML : '';
      })
      .catch(function () { /* non-fatal — keep existing markup */ });
  }

  /* ---- Add a variant to the cart (shared by single-add + sheet) --------- */
  function addVariant(variantId, btn) {
    if (!variantId) { return; }
    if (btn) { btn.classList.add('is-loading'); }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (btn) { btn.classList.remove('is-loading'); }
        if (!res.ok) { return; }
        closeUpsellSheet();
        // Hand off to the native engine: rebuild line items, subtotal, header
        // dot, keep the drawer open. Our handlers refresh count + upsell.
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: res.data, addToCartBtn: btn }
        }));
      })
      .catch(function () {
        if (btn) { btn.classList.remove('is-loading'); }
      });
  }

  /* ---- Variant-chooser sheet (multi-variant upsells) -------------------- */
  function matchVariant(scope, variants) {
    var selects = scope.querySelectorAll(SELECTORS.upsellOptionSelect);
    var chosen = Array.prototype.map.call(selects, function (s) { return s.value; });
    return variants.filter(function (v) {
      return v.options.length === chosen.length && v.options.every(function (o, i) { return o === chosen[i]; });
    })[0] || null;
  }

  // Reflect the currently selected options into the sheet's price + add button.
  function refreshSheetState() {
    var sheet = document.querySelector(SELECTORS.upsellSheet);
    if (!sheet || !sheet._variants) { return; }
    var body = sheet.querySelector('[data-upsell-sheet-body]');
    var addBtn = sheet.querySelector(SELECTORS.sheetAdd);
    var priceEl = sheet.querySelector('[data-sheet-price]');
    var match = matchVariant(body, sheet._variants);

    if (!addBtn) { return; }
    if (!match || !match.available) {
      addBtn.setAttribute('data-add-id', match ? match.id : '');
      addBtn.classList.add('is-unavailable');
      addBtn.setAttribute('aria-disabled', 'true');
    } else {
      addBtn.setAttribute('data-add-id', match.id);
      addBtn.classList.remove('is-unavailable');
      addBtn.removeAttribute('aria-disabled');
    }
    if (priceEl && match) { priceEl.innerHTML = priceHtml(match); }
  }

  function buildSheet(sheet, data) {
    var firstAvail = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];
    var sel = (firstAvail && firstAvail.options) || [];
    var addLabel = sheet.getAttribute('data-add-label') || 'Toevoegen';

    var optionsHtml = data.options.map(function (opt, i) {
      var opts = opt.values.map(function (val) {
        return '<option value="' + escapeAttr(val) + '"' + (val === sel[i] ? ' selected' : '') + '>' + escapeHtml(val) + '</option>';
      }).join('');
      return '<label class="cart-upsell__option"><span class="cart-upsell__option-name">' + escapeHtml(opt.name) +
        '</span><select class="cart-upsell__option-select" data-option-index="' + i + '" aria-label="' + escapeAttr(opt.name) + '">' + opts + '</select></label>';
    }).join('');

    sheet.querySelector('[data-upsell-sheet-body]').innerHTML =
      '<div class="cart-upsell-sheet__product">' +
        (data.image ? '<span class="cart-upsell-sheet__img"><img src="' + escapeAttr(data.image) + '" alt="" width="64" height="64" loading="lazy"></span>' : '') +
        '<div class="cart-upsell-sheet__meta">' +
          '<div class="cart-upsell-sheet__title">' + escapeHtml(data.title) + '</div>' +
          '<div class="cart-upsell-sheet__price" data-sheet-price>' + priceHtml(firstAvail) + '</div>' +
        '</div>' +
      '</div>' +
      '<div class="cart-upsell-sheet__options">' + optionsHtml + '</div>' +
      '<button type="button" class="cart-upsell-sheet__add cart-upsell__add" data-add-id="' + (firstAvail ? firstAvail.id : '') + '">' +
        '<span class="cart-upsell__add-label">' + escapeHtml(addLabel) + '</span>' +
        '<span class="cart-upsell__add-loading" aria-hidden="true"></span>' +
      '</button>';

    sheet._variants = data.variants;
    refreshSheetState();
  }

  function openUpsellSheet(card) {
    var sheet = document.querySelector(SELECTORS.upsellSheet);
    var dataEl = card.querySelector('[data-upsell-data]');
    if (!sheet || !dataEl) { return; }
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    buildSheet(sheet, data);
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
  }

  function closeUpsellSheet() {
    var sheet = document.querySelector(SELECTORS.upsellSheet);
    if (!sheet) { return; }
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
  }

  // Clicks on the upsell cards' "+ TOEVOEGEN" (these live inside #CartDrawer).
  function onUpsellClick(evt) {
    var btn = evt.target.closest(SELECTORS.upsellAdd);
    if (!btn || btn.classList.contains('cart-upsell-sheet__add')) { return; }
    evt.preventDefault();
    if (btn.classList.contains('is-loading')) { return; }

    var card = btn.closest(SELECTORS.upsellItem);
    if (card && card.querySelector('[data-upsell-data]')) {
      openUpsellSheet(card);                       // multi-variant -> choose in sheet
    } else {
      addVariant(btn.getAttribute('data-add-id'), btn); // single-variant -> add now
    }
  }

  // Quick-add buttons on product cards (collection grid, recommendations, …).
  // These live OUTSIDE #CartDrawer. Same behaviour as the upsell buttons:
  // single-variant -> add now, multi-variant -> open the shared chooser sheet.
  // After a successful add, the native engine opens the cart drawer
  // (theme.CartDrawer listens to ajaxProduct:added).
  function onQuickAddClick(evt) {
    var btn = evt.target.closest(SELECTORS.quickAdd);
    if (!btn) { return; }
    evt.preventDefault();
    evt.stopPropagation();
    if (btn.classList.contains('is-loading')) { return; }

    var card = btn.closest(SELECTORS.quickAddCard);
    if (card && card.querySelector('[data-upsell-data]')) {
      openUpsellSheet(card);                              // multi-variant -> choose in sheet
    } else {
      addVariant(btn.getAttribute('data-add-id'), btn);  // single-variant -> add now
    }
  }

  // Clicks inside the sheet. The sheet is a sibling of #CartDrawer, so we must
  // stop clicks from bubbling to the window listener that closes the drawer.
  function onSheetClick(evt) {
    evt.stopPropagation();

    var addBtn = evt.target.closest(SELECTORS.sheetAdd);
    if (addBtn) {
      evt.preventDefault();
      if (addBtn.classList.contains('is-loading') || addBtn.classList.contains('is-unavailable')) { return; }
      addVariant(addBtn.getAttribute('data-add-id'), addBtn);
      return;
    }

    if (evt.target.closest('[data-upsell-sheet-close]')) {
      evt.preventDefault();
      closeUpsellSheet();
    }
  }

  function onUpsellOptionChange(evt) {
    if (evt.target.closest(SELECTORS.upsellSheet) && evt.target.closest(SELECTORS.upsellOptionSelect)) {
      refreshSheetState();
    }
  }

  /* ---- Quantity + remove (drawer only) ---------------------------------- */
  // We take over the drawer's quantity controls instead of relying on the
  // theme's QtySelector, whose pipeline blocks quantity 0 (so items never got
  // removed). changeLine posts the new quantity and asks the native CartForm to
  // rebuild (cart:build) + refreshes our extras (cart:updated).
  function changeLine(key, qty, loadingEl) {
    if (!key) { return; }
    if (loadingEl) { loadingEl.classList.add('is-loading'); }

    var done = function (cart) {
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    };
    var fail = function () { if (loadingEl) { loadingEl.classList.remove('is-loading'); } };

    if (window.theme && theme.cart && theme.cart.changeItem) {
      theme.cart.changeItem(key, qty).then(done).catch(fail);
    } else {
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(done)
        .catch(fail);
    }
  }

  // − / + buttons. Capture phase + stopImmediatePropagation so the theme's own
  // QtySelector handler does not also fire.
  function onQtyAdjust(evt) {
    var btn = evt.target.closest(SELECTORS.qtyAdjust);
    if (!btn || !btn.closest('#CartDrawer')) { return; }
    var wrapper = btn.closest('.js-qty__wrapper');
    var input = wrapper && wrapper.querySelector(SELECTORS.qtyInput);
    if (!input) { return; }

    evt.preventDefault();
    evt.stopImmediatePropagation();

    var current = parseInt(input.value, 10);
    if (isNaN(current)) { current = 1; }
    var next = btn.classList.contains('js-qty__adjust--plus') ? current + 1 : current - 1;
    if (next < 0) { next = 0; }

    input.value = next;
    changeLine(input.getAttribute('data-id'), next, wrapper);
  }

  // Typing directly in the quantity field.
  function onQtyInput(evt) {
    var input = evt.target.closest(SELECTORS.qtyInput);
    if (!input || !input.closest('#CartDrawer')) { return; }

    evt.stopImmediatePropagation();

    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < 0) { val = 0; }
    input.value = val;
    changeLine(input.getAttribute('data-id'), val, input.closest('.js-qty__wrapper'));
  }

  // Trash / remove control.
  function onRemoveClick(evt) {
    var del = evt.target.closest(SELECTORS.removeLink);
    if (!del || !del.closest('#CartDrawer')) { return; }

    var item = del.closest('[data-key]');
    if (!item) { return; }

    evt.preventDefault();
    if (item.classList.contains('is-removing')) { return; }
    item.classList.add('is-removing');

    var input = item.querySelector(SELECTORS.qtyInput);
    changeLine(input ? input.getAttribute('data-id') : item.getAttribute('data-key'), 0, item);
  }

  /* ---- Wire up ---------------------------------------------------------- */
  function init() {
    // Delegate upsell + remove clicks (markup is replaced on every refresh).
    document.addEventListener('click', onUpsellClick);
    document.addEventListener('click', onRemoveClick);

    // Quick-add buttons on product cards anywhere on the page.
    document.addEventListener('click', onQuickAddClick);

    // Take over the drawer's quantity controls (capture phase pre-empts the
    // theme's QtySelector, which otherwise blocks removing at quantity 0).
    document.addEventListener('click', onQtyAdjust, true);
    document.addEventListener('change', onQtyInput, true);

    // The sheet is static markup (a sibling of the drawer); bind it directly so
    // its clicks are handled and stopped before they reach the drawer's close.
    var sheet = document.querySelector(SELECTORS.upsellSheet);
    if (sheet) { sheet.addEventListener('click', onSheetClick); }

    // Upsell variant dropdowns -> recompute variant id / price / availability.
    document.addEventListener('change', onUpsellOptionChange);

    // Native engine fires this after quantity changes / removals.
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt.detail && evt.detail.cart;
      if (cart && typeof cart.item_count === 'number') {
        updateCount(cart.item_count);
      } else {
        fetchCartCount();
      }
      refreshUpsell();
    });

    // Fired on every add-to-cart (product page or upsell button).
    document.addEventListener('ajaxProduct:added', function () {
      fetchCartCount();
      refreshUpsell();
    });

    // Safety net: the theme has an alternate add path (variant-selector.js)
    // that opens the drawer without firing the events above. theme.Drawers
    // dispatches this when the cart drawer opens — keep count + upsell fresh.
    document.addEventListener('drawerOpen.CartDrawer', function () {
      fetchCartCount();
      refreshUpsell();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
